import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconHistory, IconPlus, IconSearch, IconUser } from "@tabler/icons-react";
import dayjs from "dayjs";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { IEvent } from "../../../models/events";
import { IPatient } from "../../../models/patient";
import { useEventStore } from "../../../store/events.store";

const ViewPatient = () => {
  const searchPatients = useEventStore((state) => state.searchPatients);
  const getPatient = useEventStore((state) => state.getPatient);
  const getPatientHistory = useEventStore((state) => state.getPatientHistory);
  const getEvents = useEventStore((state) => state.getEvents);
  const addPatient = useEventStore((state) => state.addPatient);
  const events = useEventStore((state) => state.events);
  const patientsFromSearch = useEventStore((state) => state.patientsFromSearch);
  const [searchValue, setSearchValue] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [historyLoadingId, setHistoryLoadingId] = useState<string | null>(null);
  const [addLoading, setAddLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<IPatient | null>(null);
  const [patientHistory, setPatientHistory] = useState<IEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const isOfficeStaffOnly =
    selectedPatient?.officeStaffOnly === true || selectedPatient?.officeStaffOnly === "0";

  useEffect(() => {
    if (events.length === 0) {
      getEvents();
    }
  }, [events.length, getEvents]);

  const upcomingEvents = useMemo(() => {
    if (!selectedPatient) {
      return [];
    }

    const existingEventIds = new Set(selectedPatient.events ?? []);

    return events.filter(
      (event) =>
        dayjs(event.date).isAfter(dayjs()) &&
        (event.patientTotal ?? 0) < event.limit &&
        !existingEventIds.has(event.uid!),
    );
  }, [events, selectedPatient]);

  const selectedUpcomingEvent = useMemo(
    () => upcomingEvents.find((event) => event.uid === selectedEventId) ?? null,
    [selectedEventId, upcomingEvents],
  );

  const handleSearch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSelectedPatient(null);
    setPatientHistory([]);
    setHistoryLoadingId(null);
    setSelectedEventId(null);
    setSearchLoading(true);
    await searchPatients(searchValue);
    setSearchLoading(false);
  };

  const handleLoadHistory = async (patientId: string) => {
    setHistoryLoadingId(patientId);

    const patient = await getPatient(patientId);
    if (!patient) {
      setSelectedPatient(null);
      setPatientHistory([]);
      setHistoryLoadingId(null);
      return;
    }

    const history = await getPatientHistory(patient.events ?? []);
    setSelectedPatient(patient);
    setPatientHistory(history);
    setSelectedEventId(null);
    setHistoryLoadingId(null);
  };

  return (
    <Stack
      gap="lg"
      style={{ height: "100%", minHeight: 0, overflowY: "auto", paddingRight: 4 }}
    >
        <div>
          {/* <Title order={3}>Patient sign up history</Title> */}
          <Text c="var(--my-dark)" size="sm" mt={4}>
            Search by patient name, phone number, or email, then load the appointments tied to that patient.
          </Text>
        </div>

        <form onSubmit={handleSearch}>
          <Group align="flex-end">
            <TextInput
              value={searchValue}
              onChange={(event) => setSearchValue(event.currentTarget.value)}
              label="Search patient"
              placeholder="Jane Doe, phone, or email"
              style={{ flex: 1 }}
            />
            <Button
              type="submit"
              color="clinic"
              leftSection={<IconSearch size={16} />}
              loading={searchLoading}
              disabled={searchValue.trim().length === 0}
            >
              Search
            </Button>
          </Group>
        </form>

        {searchLoading && (
          <Center>
            <Loader color="clinic" />
          </Center>
        )}

        {!searchLoading && searchValue.trim().length > 0 && patientsFromSearch.length === 0 && (
          <Alert color="accent" variant="light">
            No patients matched that search.
          </Alert>
        )}

        {patientsFromSearch.length > 0 && (
          <Stack gap="sm">
            {patientsFromSearch.map((patient) => (
              <Card key={patient.uid} withBorder radius="xl" shadow="xs">
                <Group justify="space-between" align="flex-start">
                  <div>
                    <Group gap="xs">
                      <IconUser size={18} />
                      <Text fw={600}>
                        {patient.firstName} {patient.lastName}
                      </Text>
                    </Group>
                    <Text size="sm" c="var(--my-dark)" mt={4}>
                      {patient.phone || "No phone on file"}
                    </Text>
                    {patient.email && (
                      <Text size="sm" c="var(--my-dark)">
                        {patient.email}
                      </Text>
                    )}
                  </div>
                  <Button
                    variant="light"
                    color="clinic"
                    leftSection={<IconHistory size={16} />}
                    loading={historyLoadingId === patient.uid}
                    onClick={() => handleLoadHistory(patient.uid!)}
                  >
                    Load History
                  </Button>
                </Group>
              </Card>
            ))}
          </Stack>
        )}

        {selectedPatient && (
          <Card withBorder radius="xl" shadow="xs" padding="lg">
            <Stack gap="md">
              <div>
                <Title order={4}>
                  {selectedPatient.firstName} {selectedPatient.lastName}
                </Title>
                <Group gap="xs" mt="xs">
                  <Badge color="clinic" variant="light">
                    {patientHistory.length} sign ups
                  </Badge>
                  {selectedPatient.isNewPatient === "yes" && (
                    <Badge color="green" variant="light">
                      New patient
                    </Badge>
                  )}
                  {isOfficeStaffOnly && (
                    <Badge color="grape" variant="light">
                      Questions only
                    </Badge>
                  )}
                </Group>
                <Text size="sm" c="var(--my-dark)" mt={8}>
                  DOB: {selectedPatient.dob ? dayjs(selectedPatient.dob).format("MM/DD/YYYY") : "Not available"}
                </Text>
                {selectedPatient.phone && (
                  <Text size="sm" c="var(--my-dark)">
                    Phone: {selectedPatient.phone}
                  </Text>
                )}
                {selectedPatient.email && (
                  <Text size="sm" c="var(--my-dark)">
                    Email: {selectedPatient.email}
                  </Text>
                )}
              </div>

              <Card withBorder radius="lg" padding="md" bg="rgba(255, 255, 255, 0.45)">
                <Stack gap="sm">
                  <div>
                    <Title order={5}>Add to upcoming event</Title>
                    <Text size="sm" c="var(--my-dark)" mt={4}>
                      Choose an available upcoming event to register this patient.
                    </Text>
                  </div>

                  <Select
                    label="Upcoming event"
                    placeholder="Select an event"
                    data={upcomingEvents.map((event) => ({
                      value: event.uid!,
                      label: `${dayjs(event.date).format("MMM DD, YYYY h:mm A")} - ${event.type} (${event.patientTotal ?? 0}/${event.limit})`,
                    }))}
                    value={selectedEventId}
                    onChange={setSelectedEventId}
                    searchable
                    nothingFoundMessage="No upcoming events available"
                  />

                  <Button
                    color="clinic"
                    leftSection={<IconPlus size={16} />}
                    loading={addLoading}
                    disabled={!selectedUpcomingEvent}
                    onClick={async () => {
                      if (!selectedPatient || !selectedUpcomingEvent) {
                        return;
                      }

                      setAddLoading(true);
                      const added = await addPatient(selectedUpcomingEvent, selectedPatient);
                      if (added) {
                        const patient = await getPatient(selectedPatient.uid!);
                        if (patient) {
                          const history = await getPatientHistory(patient.events ?? []);
                          setSelectedPatient(patient);
                          setPatientHistory(history);
                          setSelectedEventId(null);
                        }
                      }
                      setAddLoading(false);
                    }}
                  >
                    Add Patient
                  </Button>

                  {upcomingEvents.length === 0 && (
                    <Alert color="clinic" variant="light">
                      This patient is already registered for all available upcoming events.
                    </Alert>
                  )}
                </Stack>
              </Card>

              <Divider />

              <div>
                <Title order={5}>Appointment history</Title>
                <Text size="sm" c="var(--my-dark)" mt={4}>
                  Showing every event currently linked to this patient record.
                </Text>
              </div>

              {patientHistory.length === 0 && (
                <Alert color="clinic" variant="light">
                  No sign up history found for this patient.
                </Alert>
              )}

              {patientHistory.map((event) => (
                <Card key={event.uid} withBorder radius="lg" padding="md">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text fw={600}>{event.type}</Text>
                      <Badge color="clinic" variant="light">
                        {dayjs(event.date).format("MMM DD, YYYY")}
                      </Badge>
                    </Group>
                    <Text size="sm" c="var(--my-dark)">
                      {dayjs(event.date).format("h:mm A")}
                    </Text>
                    <Text size="sm" c="var(--my-dark)">
                      Registered patients: {event.patientTotal ?? 0} / {event.limit}
                    </Text>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Card>
        )}
      </Stack>
  );
};

export default ViewPatient;
